import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, Loader2, Plus, Minus, Tag, Users, Baby, CreditCard, Smartphone, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { roomsApi, billingApi, promotionsApi, paymentsApi, addonsApi, bookingsApi } from '../services/api';
import BookingDatePicker from '../components/BookingDatePicker';
import { rooms as mockRooms } from '../mockData';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabaseClient';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useSEO } from '../hooks/useSEO';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  
  const [step, setStep] = useState(1);
  const [selectedRoomId, setSelectedRoomId] = useState(searchParams.get('room') || '');
  const [currency, setCurrency] = useState('USD');
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useSEO({
    title: 'Book Your Stay | OlivOak Villa',
    description: 'Reserve your luxury room at OlivOak Villa, Harare. Choose your dates, select your room, and complete your booking online.'
  });
  
  // Payment state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('web');
  const [mobilePaymentPhone, setMobilePaymentPhone] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [createdBookingRef, setCreatedBookingRef] = useState(null);
  const [isMockMode, setIsMockMode] = useState(true);
  
  // Billing calculator state
  const [adults, setAdults] = useState(1);
  const [childrenAges, setChildrenAges] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [autoAppliedPromo, setAutoAppliedPromo] = useState(null);
  const [billingResult, setBillingResult] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [roomTypeInfo, setRoomTypeInfo] = useState(null); // Room type pricing info
  
  // Add-ons state
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]); // [{addon_id, quantity}]
  
  // Room availability / booked dates state
  const [bookedRanges, setBookedRanges] = useState([]);
  const [loadingBookedDates, setLoadingBookedDates] = useState(false);
  
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    firstName: user?.user_metadata?.full_name?.split(' ')[0] || '',
    lastName: user?.user_metadata?.full_name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: '',
    specialRequests: ''
  });

  useEffect(() => {
    fetchRooms();
    fetchAddons();
  }, []);

  const fetchAddons = async () => {
    try {
      const data = await addonsApi.getAll(true);
      setAvailableAddons(data);
    } catch (err) {
      console.error('Failed to fetch add-ons:', err);
    }
  };

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => {
      const existing = prev.find(a => a.addon_id === addonId);
      if (existing) {
        return prev.filter(a => a.addon_id !== addonId);
      }
      return [...prev, { addon_id: addonId, quantity: 1 }];
    });
  };

  const updateAddonQuantity = (addonId, quantity) => {
    setSelectedAddons(prev =>
      prev.map(a => a.addon_id === addonId ? { ...a, quantity: Math.max(1, quantity) } : a)
    );
  };

  useEffect(() => {
    // Pre-fill user data when auth changes
    if (user) {
      setBookingData(prev => ({
        ...prev,
        firstName: prev.firstName || user?.user_metadata?.full_name?.split(' ')[0] || '',
        lastName: prev.lastName || user?.user_metadata?.full_name?.split(' ').slice(1).join(' ') || '',
        email: prev.email || user?.email || ''
      }));
    }
  }, [user]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const data = await roomsApi.getAll();
      
      // Transform API data
      const transformedRooms = data.map(room => ({
        id: room.slug || room.id,
        dbId: room.id,
        name: room.name,
        description: room.description,
        shortDescription: room.short_description,
        occupancy: { max: room.max_occupancy, beds: room.beds },
        size: `${room.size_sqm} sqm`,
        price: {
          USD: room.price_usd,
          ZAR: room.price_zar,
          GBP: room.price_gbp,
          EUR: room.price_eur
        },
        images: room.images || [],
        amenities: room.amenities || [],
        available: room.available,
        roomTypeId: room.room_type_id // Include room_type_id for billing
      }));
      
      setRooms(transformedRooms.length > 0 ? transformedRooms : mockRooms);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
      setRooms(mockRooms);
    } finally {
      setLoading(false);
    }
  };

  const selectedRoom = rooms.find(r => r.id === selectedRoomId);

  // Fetch room type info when room changes to get occupancy limits
  const fetchRoomTypeInfo = useCallback(async (roomTypeId) => {
    if (!roomTypeId) {
      setRoomTypeInfo(null);
      return;
    }
    try {
      const info = await billingApi.getPricingInfo(roomTypeId);
      // Extract room_type from response
      const roomType = info.room_type || info;
      console.log('Room type info fetched:', roomType); // Debug log
      setRoomTypeInfo(roomType);
    } catch (err) {
      console.error('Failed to fetch room type info:', err);
      setRoomTypeInfo(null);
    }
  }, []);

  // Track whether we've already fetched booked dates for the current room
  const [bookedDatesFetched, setBookedDatesFetched] = useState(false);

  // Fetch booked dates for a room (for calendar greyed-out dates)
  const fetchBookedDates = useCallback(async (roomDbId) => {
    if (!roomDbId) {
      setBookedRanges([]);
      setBookedDatesFetched(false);
      return;
    }
    setLoadingBookedDates(true);
    try {
      const data = await bookingsApi.getBookedDates(roomDbId);
      setBookedRanges(data.booked_ranges || []);
    } catch (err) {
      console.error('Failed to fetch booked dates:', err);
      setBookedRanges([]);
    } finally {
      setLoadingBookedDates(false);
      setBookedDatesFetched(true);
    }
  }, []);

  // Handle room selection change - fetch room type info and adjust occupancy
  const handleRoomChange = (roomId) => {
    setSelectedRoomId(roomId);
    const room = rooms.find(r => r.id === roomId);
    console.log('Selected room:', room); // Debug: check if room has roomTypeId
    
    // Fetch room type info for occupancy limits
    if (room?.roomTypeId) {
      console.log('Fetching room type info for:', room.roomTypeId);
      fetchRoomTypeInfo(room.roomTypeId);
    } else {
      console.log('No roomTypeId found on room');
      setRoomTypeInfo(null);
    }
    
    // Fetch booked dates for the calendar
    const dbId = room?.dbId || roomId;
    setBookedDatesFetched(false);
    fetchBookedDates(dbId);
    
    // Clear dates when switching rooms (previously selected dates may conflict)
    setBookingData(prev => ({ ...prev, checkIn: '', checkOut: '' }));
  };

  // Calculate occupancy limits from room type info (room type is the authority)
  const maxAdults = roomTypeInfo?.max_adults || 2;
  const maxOccupancy = roomTypeInfo?.max_occupancy || maxAdults;
  const maxChildren = Math.max(0, maxOccupancy - adults);
  
  // Debug log
  console.log('Occupancy limits:', { maxAdults, maxOccupancy, maxChildren, adults, roomTypeInfo });

  // Fetch room type info when selectedRoom changes (handles pre-selected room on page load)
  useEffect(() => {
    if (selectedRoom?.roomTypeId && !roomTypeInfo) {
      console.log('Page load: fetching room type info for pre-selected room:', selectedRoom.roomTypeId);
      fetchRoomTypeInfo(selectedRoom.roomTypeId);
    }
  }, [selectedRoom, roomTypeInfo, fetchRoomTypeInfo]);

  // Fetch booked dates when selectedRoom changes (handles pre-selected room on page load)
  useEffect(() => {
    if (selectedRoom && !bookedDatesFetched && !loadingBookedDates) {
      const dbId = selectedRoom.dbId || selectedRoom.id;
      fetchBookedDates(dbId);
    }
  }, [selectedRoom, bookedDatesFetched, loadingBookedDates, fetchBookedDates]);

  // Adjust adults and children when room type info changes (only runs when roomTypeInfo changes)
  useEffect(() => {
    if (roomTypeInfo) {
      const rtMaxAdults = roomTypeInfo.max_adults;
      const rtMaxOccupancy = roomTypeInfo.max_occupancy || rtMaxAdults;
      
      // Adjust adults if exceeding max_adults
      setAdults(prev => {
        const newAdults = prev > rtMaxAdults ? rtMaxAdults : prev;
        // Also adjust children based on new adults value
        setChildrenAges(prevChildren => {
          const maxAllowedChildren = Math.max(0, rtMaxOccupancy - newAdults);
          return prevChildren.length > maxAllowedChildren ? prevChildren.slice(0, maxAllowedChildren) : prevChildren;
        });
        return newAdults;
      });
    }
  }, [roomTypeInfo]);

  const calculateNights = useCallback(() => {
    if (!bookingData.checkIn || !bookingData.checkOut) return 0;
    const start = new Date(bookingData.checkIn);
    const end = new Date(bookingData.checkOut);
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 0;
  }, [bookingData.checkIn, bookingData.checkOut]);

  // Calculate billing using the billing API
  const calculateBilling = useCallback(async () => {
    const nights = calculateNights();
    
    if (!selectedRoom || nights <= 0) {
      setBillingResult(null);
      return;
    }
    
    // If no room_type_id, use fallback calculation
    if (!selectedRoom.roomTypeId) {
      const subtotal = selectedRoom.price[currency] * nights;
      const tax = subtotal * 0.02;
      setBillingResult({
        subtotal,
        total: subtotal + tax,
        nights,
        breakdown: {
          base_rate: subtotal,
          extra_adults: 0,
          children: 0,
          promotion_discount: 0,
          taxes: [{ name: 'ZTA Tax', rate_percent: 2, amount: tax }],
          tax_total: tax
        },
        usedFallback: true
      });
      return;
    }
    
    setCalculatingPrice(true);
    try {
      const result = await billingApi.calculate({
        room_type_id: selectedRoom.roomTypeId,
        adults: adults,
        children_ages: childrenAges,
        nights: nights,
        promotion_code: promoApplied?.code || null,
        addons: selectedAddons.length > 0 ? selectedAddons : []
      });
      
      // Detect auto-applied promotion from billing engine
      if (result.applied_promotion && result.applied_promotion.auto_applied) {
        setAutoAppliedPromo(result.applied_promotion);
      } else {
        setAutoAppliedPromo(null);
      }

      setBillingResult({
        ...result,
        usedFallback: false
      });
    } catch (err) {
      console.error('Billing calculation failed:', err);
      // Fallback to simple calculation
      const subtotal = selectedRoom.price[currency] * nights;
      const tax = subtotal * 0.02;
      setBillingResult({
        subtotal,
        total: subtotal + tax,
        nights,
        breakdown: {
          base_rate: subtotal,
          extra_adults: 0,
          children: 0,
          promotion_discount: 0,
          taxes: [{ name: 'ZTA Tax', rate_percent: 2, amount: tax }],
          tax_total: tax
        },
        usedFallback: true
      });
    } finally {
      setCalculatingPrice(false);
    }
  }, [selectedRoom, calculateNights, adults, childrenAges, promoApplied, selectedAddons, currency]);

  // Recalculate billing when inputs change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      calculateBilling();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [calculateBilling]);

  // Validate promotion code
  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }
    
    try {
      const promo = await promotionsApi.validate(promoCode.trim().toUpperCase());
      setPromoApplied(promo);
      setPromoError('');
      toast({
        title: "Promo Applied!",
        description: `${promo.name} - ${promo.discount_type === 'PERCENT' ? promo.discount_value + '%' : '$' + promo.discount_value} off`,
      });
    } catch (err) {
      setPromoError(err.message || 'Invalid promo code');
      setPromoApplied(null);
    }
  };

  const removePromoCode = () => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  };

  // Child age management
  const addChild = () => {
    if (childrenAges.length < 4) {
      setChildrenAges([...childrenAges, 5]); // Default age 5
    }
  };

  const removeChild = (index) => {
    setChildrenAges(childrenAges.filter((_, i) => i !== index));
  };

  const updateChildAge = (index, age) => {
    const newAges = [...childrenAges];
    newAges[index] = parseInt(age);
    setChildrenAges(newAges);
  };

  // Legacy support
  const calculateTotal = () => {
    if (billingResult) {
      return {
        subtotal: billingResult.subtotal,
        tax: billingResult.breakdown?.tax_total || 0,
        total: billingResult.total,
        nights: billingResult.nights || calculateNights()
      };
    }
    if (!selectedRoom) return { subtotal: 0, tax: 0, total: 0, nights: 0 };
    const nights = calculateNights();
    const subtotal = selectedRoom.price[currency] * nights;
    const tax = subtotal * 0.02;
    return { subtotal, tax, total: subtotal + tax, nights };
  };

  const handleNext = () => {
    if (step === 1) {
      if (!selectedRoomId || !bookingData.checkIn || !bookingData.checkOut || adults < 1) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        return;
      }
      if (calculateNights() <= 0) {
        toast({
          title: "Invalid Dates",
          description: "Check-out date must be after check-in date.",
          variant: "destructive"
        });
        return;
      }
    }
    
    if (step === 2) {
      if (!bookingData.firstName || !bookingData.lastName || !bookingData.email || !bookingData.phone) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        return;
      }
    }

    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Fetch payment methods on mount
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await paymentsApi.getMethods();
        setIsMockMode(response.mock_mode === true);
      } catch (err) {
        console.log('Could not fetch payment methods, using default');
      }
    };
    fetchPaymentMethods();
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      // Try to get session token
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      // Build query parameters for the API
      const totalGuests = adults + childrenAges.length;
      const params = new URLSearchParams({
        room_id: selectedRoom.dbId || selectedRoom.id,
        check_in: bookingData.checkIn,
        check_out: bookingData.checkOut,
        guests: totalGuests.toString(),
        currency: currency,
        guest_first_name: bookingData.firstName,
        guest_last_name: bookingData.lastName,
        guest_email: bookingData.email,
        guest_phone: bookingData.phone
      });
      
      if (bookingData.specialRequests) {
        params.append('special_requests', bookingData.specialRequests);
      }
      
      // Add billing details
      if (promoApplied) {
        params.append('promotion_code', promoApplied.code);
      }
      params.append('adults', adults.toString());
      if (childrenAges.length > 0) {
        params.append('children_ages', childrenAges.join(','));
      }
      
      // Pass the billing-calculated totals so the backend stores the correct amounts
      if (billingResult) {
        params.append('billing_subtotal', billingResult.subtotal.toFixed(2));
        params.append('billing_tax', (billingResult.breakdown?.tax_total || 0).toFixed(2));
        params.append('billing_total', billingResult.total.toFixed(2));
      }
      
      // Add add-on details for the email notification
      if (selectedAddons.length > 0 && billingResult?.breakdown?.addons) {
        const addonSummaries = billingResult.breakdown.addons.map(a => ({
          name: a.name,
          quantity: a.quantity,
          total: a.total
        }));
        params.append('addons_json', JSON.stringify(addonSummaries));
      }
      
      const response = await fetch(`${API_URL}/api/bookings?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      if (response.ok) {
        const booking = await response.json();
        
        // Store booking info and show payment dialog
        setCreatedBookingId(booking.id);
        setCreatedBookingRef(booking.booking_reference);
        setShowPaymentDialog(true);
      } else {
        // Booking creation failed (likely not authenticated)
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 401) {
          toast({
            title: "Login Required",
            description: "Please sign in to complete your booking and make a payment.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Booking Failed",
            description: errorData.detail || "Could not create booking. Please try again.",
            variant: "destructive"
          });
        }
      }
    } catch (err) {
      console.error('Booking failed:', err);
      toast({
        title: "Booking Error",
        description: "Something went wrong. Please check your connection and try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async () => {
    setProcessingPayment(true);
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        toast({
          title: "Login Required",
          description: "Please sign in to make a payment.",
          variant: "destructive"
        });
        setProcessingPayment(false);
        return;
      }
      
      if (!createdBookingId) {
        toast({
          title: "Booking Error",
          description: "No booking found. Please create a booking first.",
          variant: "destructive"
        });
        setProcessingPayment(false);
        return;
      }
      
      // For mobile payments, validate phone number
      if (paymentMethod === 'ecocash' && !mobilePaymentPhone) {
        toast({
          title: "Phone Number Required",
          description: "Please enter your mobile money phone number",
          variant: "destructive"
        });
        setProcessingPayment(false);
        return;
      }
      
      const response = await paymentsApi.initiate(
        createdBookingId,
        paymentMethod,
        mobilePaymentPhone,
        token
      );
      
      if (response.success) {
        if (paymentMethod === 'web' && response.payment_url) {
          // Redirect to Paynow payment page
          toast({
            title: "Redirecting to Payment",
            description: "You will be redirected to Paynow to complete your payment",
          });
          
          setTimeout(() => {
            window.location.href = response.payment_url;
          }, 1000);
        } else {
          // Mobile payment - navigate to status page
          toast({
            title: "Payment Initiated",
            description: "Please check your phone to complete the payment",
          });
          
          setTimeout(() => {
            navigate(`/payment-status?ref=${createdBookingRef}`);
          }, 1500);
        }
      } else {
        // Mock mode or error - go to confirmation
        toast({
          title: response.mock ? "Mock Payment" : "Payment Initiated",
          description: response.message || "Processing your booking...",
        });
        
        setTimeout(() => {
          navigate(`/booking-confirmation?ref=${createdBookingRef}`);
        }, 1500);
      }
    } catch (err) {
      console.error('Payment initiation failed:', err);
      toast({
        title: "Payment Error",
        description: err.message || "Could not initiate payment. Your booking has been saved.",
        variant: "destructive"
      });
      
      // Still navigate to confirmation even if payment fails
      setTimeout(() => {
        navigate(`/booking-confirmation?ref=${createdBookingRef}`);
      }, 2000);
    } finally {
      setProcessingPayment(false);
      setShowPaymentDialog(false);
    }
  };

  const getCurrencySymbol = (curr) => {
    const symbols = { USD: '$', ZAR: 'R', GBP: '£', EUR: '€' };
    return symbols[curr] || '$';
  };

  const { subtotal, tax, total, nights } = calculateTotal();

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin" size={40} style={{ color: 'var(--olive-primary)' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="booking-page">
      <section className="py-12">
        <div className="container max-w-5xl">
          <button
            onClick={() => navigate('/rooms')}
            className="flex items-center gap-2 mb-8 btn-secondary"
            data-testid="back-to-rooms"
          >
            <ArrowLeft size={16} />
            Back to Rooms
          </button>

          <h1 className="hero-medium mb-12 text-center">Complete Your Booking</h1>

          {/* Progress Steps */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-4">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div
                    className="flex items-center justify-center w-12 h-12 border-2 transition-all"
                    style={{
                      borderColor: step >= s ? 'var(--interactive-base)' : 'var(--border-medium)',
                      backgroundColor: step >= s ? 'var(--interactive-base)' : 'transparent',
                      color: step >= s ? 'var(--bg-primary)' : 'var(--text-secondary)'
                    }}
                    data-testid={`step-${s}`}
                  >
                    {step > s ? <Check size={20} /> : s}
                  </div>
                  {s < 3 && (
                    <div
                      className="w-16 h-0.5"
                      style={{
                        backgroundColor: step > s ? 'var(--interactive-base)' : 'var(--border-medium)'
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2">
              {/* Step 1: Room & Dates */}
              {step === 1 && (
                <div
                  className="p-8"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <h2 className="heading-2 mb-6">Select Room & Dates</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="room">Room</Label>
                      <Select value={selectedRoomId} onValueChange={handleRoomChange}>
                        <SelectTrigger id="room" style={{ borderRadius: 0 }} data-testid="room-select">
                          <SelectValue placeholder="Select a room" />
                        </SelectTrigger>
                        <SelectContent>
                          {rooms.map((room) => (
                            <SelectItem key={room.id} value={room.id}>
                              {room.name} - {getCurrencySymbol(currency)}{room.price[currency]}/night
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="checkIn">Check-In</Label>
                        <BookingDatePicker
                          value={bookingData.checkIn}
                          onChange={(dateStr) => {
                            setBookingData(prev => ({
                              ...prev,
                              checkIn: dateStr,
                              // Reset check-out if it's before the new check-in
                              checkOut: prev.checkOut && prev.checkOut <= dateStr ? '' : prev.checkOut
                            }));
                          }}
                          bookedRanges={bookedRanges}
                          placeholder="Select check-in"
                          disabled={!selectedRoomId || loadingBookedDates}
                          testId="check-in-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="checkOut">Check-Out</Label>
                        <BookingDatePicker
                          value={bookingData.checkOut}
                          onChange={(dateStr) => setBookingData({ ...bookingData, checkOut: dateStr })}
                          bookedRanges={bookedRanges}
                          minDate={bookingData.checkIn ? new Date(bookingData.checkIn + 'T00:00:00') : undefined}
                          placeholder="Select check-out"
                          disabled={!selectedRoomId || !bookingData.checkIn || loadingBookedDates}
                          testId="check-out-input"
                        />
                      </div>
                    </div>
                    {loadingBookedDates && (
                      <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                        <Loader2 className="animate-spin" size={12} />
                        Loading availability...
                      </p>
                    )}

                    {/* Adults */}
                    <div>
                      <Label className="flex items-center gap-2">
                        <Users size={16} />
                        Adults
                        {roomTypeInfo && (
                          <span className="text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>
                            (max {maxAdults})
                          </span>
                        )}
                      </Label>
                      <div className="flex items-center gap-3 mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setAdults(Math.max(1, adults - 1))}
                          disabled={adults <= 1}
                          style={{ borderRadius: 0 }}
                        >
                          <Minus size={16} />
                        </Button>
                        <span className="w-12 text-center heading-3">{adults}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setAdults(Math.min(maxAdults, adults + 1))}
                          disabled={adults >= maxAdults}
                          style={{ borderRadius: 0 }}
                        >
                          <Plus size={16} />
                        </Button>
                      </div>
                    </div>

                    {/* Children */}
                    <div>
                      <Label className="flex items-center gap-2">
                        <Baby size={16} />
                        Children
                        {selectedRoom && maxOccupancy > maxAdults && (
                          <span className="text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>
                            (max {maxChildren} with {adults} adult{adults > 1 ? 's' : ''})
                          </span>
                        )}
                      </Label>
                      <div className="mt-2 space-y-3">
                        {childrenAges.map((age, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <Select
                              value={age.toString()}
                              onValueChange={(value) => updateChildAge(index, value)}
                            >
                              <SelectTrigger style={{ borderRadius: 0, width: '120px' }}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 18 }, (_, i) => (
                                  <SelectItem key={i} value={i.toString()}>
                                    {i} {i === 1 ? 'year' : 'years'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => removeChild(index)}
                              style={{ borderRadius: 0 }}
                            >
                              <Minus size={16} />
                            </Button>
                          </div>
                        ))}
                        {childrenAges.length < maxChildren && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={addChild}
                            className="flex items-center gap-2"
                            style={{ borderRadius: 0 }}
                          >
                            <Plus size={16} />
                            Add Child
                          </Button>
                        )}
                        {childrenAges.length === 0 && maxChildren > 0 && (
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            No children added
                          </p>
                        )}
                        {maxChildren === 0 && (
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            Maximum occupancy reached with {adults} adult{adults > 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="currency">Currency</Label>
                      <div
                        className="flex items-center h-10 w-full px-3 text-sm"
                        style={{
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--border-light)',
                          color: 'var(--text-primary)',
                          borderRadius: 0,
                        }}
                        data-testid="currency-select"
                      >
                        USD - US Dollar
                      </div>
                    </div>

                    {/* Promo Code */}
                    <div>
                      <Label className="flex items-center gap-2">
                        <Tag size={16} />
                        Promo Code (Optional)
                      </Label>
                      {promoApplied ? (
                        <div
                          className="mt-2 p-3 flex items-center justify-between"
                          style={{
                            backgroundColor: 'var(--bg-primary)',
                            border: '1px solid var(--interactive-base)'
                          }}
                        >
                          <div>
                            <span className="body-regular" style={{ color: 'var(--interactive-base)' }}>
                              ✓ {promoApplied.code}
                            </span>
                            <span className="body-small ml-2" style={{ color: 'var(--text-secondary)' }}>
                              ({promoApplied.discount_type === 'PERCENT' 
                                ? `${promoApplied.discount_value}% off` 
                                : `$${promoApplied.discount_value} off`})
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={removePromoCode}
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-2">
                          <Input
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            placeholder="Enter code"
                            style={{ borderRadius: 0 }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={applyPromoCode}
                            style={{ borderRadius: 0 }}
                          >
                            Apply
                          </Button>
                        </div>
                      )}
                      {promoError && (
                        <p className="text-sm mt-1" style={{ color: '#dc2626' }}>
                          {promoError}
                        </p>
                      )}
                      {/* Auto-applied promotion banner */}
                      {!promoApplied && autoAppliedPromo && (
                        <div
                          className="mt-2 p-3"
                          style={{
                            backgroundColor: 'var(--bg-primary)',
                            border: '1px solid #8b5cf6'
                          }}
                        >
                          <span className="body-regular" style={{ color: '#8b5cf6' }}>
                            ✓ Auto-applied: {autoAppliedPromo.name}
                          </span>
                          <span className="body-small ml-2" style={{ color: 'var(--text-secondary)' }}>
                            ({autoAppliedPromo.discount_type === 'PERCENT' 
                              ? `${autoAppliedPromo.discount_value}% off` 
                              : `$${autoAppliedPromo.discount_value} off`}
                            &nbsp;— no code needed)
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Add-ons Selection */}
                    {availableAddons.length > 0 && (
                      <div>
                        <Label className="flex items-center gap-2">
                          <Package size={16} />
                          Add-on Services (Optional)
                        </Label>
                        <div className="mt-2 space-y-2">
                          {availableAddons.map((addon) => {
                            const selected = selectedAddons.find(a => a.addon_id === addon.id);
                            return (
                              <div
                                key={addon.id}
                                className="p-3 flex items-center justify-between"
                                style={{
                                  backgroundColor: selected ? 'var(--bg-primary)' : 'transparent',
                                  border: selected 
                                    ? '1px solid var(--interactive-base)' 
                                    : '1px solid var(--border-light)',
                                  cursor: 'pointer'
                                }}
                                onClick={() => toggleAddon(addon.id)}
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={!!selected}
                                    onChange={() => toggleAddon(addon.id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div>
                                    <span className="body-regular">{addon.name}</span>
                                    <span className="body-small ml-2" style={{ color: 'var(--text-secondary)' }}>
                                      ${addon.unit_price}
                                      {addon.pricing_type === 'PER_PERSON' ? '/person' : ' flat'}
                                      {addon.per_night ? '/night' : ''}
                                    </span>
                                    {addon.description && (
                                      <p className="text-xs" style={{ color: 'var(--text-light)' }}>
                                        {addon.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {selected && (
                                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                      {addon.pricing_type === 'PER_PERSON' ? 'People:' : 'Qty:'}
                                    </span>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => updateAddonQuantity(addon.id, selected.quantity - 1)}
                                      disabled={selected.quantity <= 1}
                                      style={{ borderRadius: 0 }}
                                    >
                                      <Minus size={12} />
                                    </Button>
                                    <span className="w-6 text-center text-sm font-medium">{selected.quantity}</span>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => updateAddonQuantity(addon.id, selected.quantity + 1)}
                                      style={{ borderRadius: 0 }}
                                    >
                                      <Plus size={12} />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Guest Details */}
              {step === 2 && (
                <div
                  className="p-8"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <h2 className="heading-2 mb-6">Guest Information</h2>
                  
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name</Label>
                        <Input
                          id="firstName"
                          value={bookingData.firstName}
                          onChange={(e) => setBookingData({ ...bookingData, firstName: e.target.value })}
                          style={{ borderRadius: 0 }}
                          data-testid="first-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          value={bookingData.lastName}
                          onChange={(e) => setBookingData({ ...bookingData, lastName: e.target.value })}
                          style={{ borderRadius: 0 }}
                          data-testid="last-name-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={bookingData.email}
                        onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                        style={{ borderRadius: 0 }}
                        data-testid="email-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={bookingData.phone}
                        onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                        placeholder="+263..."
                        style={{ borderRadius: 0 }}
                        data-testid="phone-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="specialRequests">Special Requests (Optional)</Label>
                      <textarea
                        id="specialRequests"
                        value={bookingData.specialRequests}
                        onChange={(e) => setBookingData({ ...bookingData, specialRequests: e.target.value })}
                        rows={4}
                        className="w-full p-3 border"
                        style={{
                          borderColor: 'var(--border-medium)',
                          borderRadius: 0,
                          fontFamily: "'Inter', sans-serif",
                          fontSize: '14px'
                        }}
                        placeholder="Any special requests or requirements?"
                        data-testid="special-requests-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Confirmation */}
              {step === 3 && (
                <div
                  className="p-8"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <h2 className="heading-2 mb-6">Review & Confirm</h2>
                  
                  <div className="space-y-6">
                    <div
                      className="p-6"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        border: '1px solid var(--border-light)'
                      }}
                    >
                      <h3 className="heading-3 mb-4">Booking Details</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Room:</span>
                          <span className="body-regular" data-testid="confirm-room">{selectedRoom?.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Check-In:</span>
                          <span className="body-regular" data-testid="confirm-checkin">{new Date(bookingData.checkIn).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Check-Out:</span>
                          <span className="body-regular" data-testid="confirm-checkout">{new Date(bookingData.checkOut).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Adults:</span>
                          <span className="body-regular">{adults}</span>
                        </div>
                        {childrenAges.length > 0 && (
                          <div className="flex justify-between">
                            <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Children:</span>
                            <span className="body-regular">{childrenAges.length} ({childrenAges.join(', ')} yrs)</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Nights:</span>
                          <span className="body-regular">{calculateNights()}</span>
                        </div>
                        {promoApplied && (
                          <div className="flex justify-between">
                            <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Promo Code:</span>
                            <span className="body-regular" style={{ color: 'var(--interactive-base)' }}>{promoApplied.code}</span>
                          </div>
                        )}
                        {selectedAddons.length > 0 && (
                          <div className="pt-2" style={{ borderTop: '1px dashed var(--border-light)' }}>
                            <span className="body-small font-medium">Add-ons:</span>
                            {selectedAddons.map((sel) => {
                              const addon = availableAddons.find(a => a.id === sel.addon_id);
                              if (!addon) return null;
                              return (
                                <div key={sel.addon_id} className="flex justify-between mt-1">
                                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                                    {addon.name}
                                  </span>
                                  <span className="body-regular">
                                    {addon.pricing_type === 'PER_PERSON' 
                                      ? `${sel.quantity} person${sel.quantity > 1 ? 's' : ''}` 
                                      : `x${sel.quantity}`}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className="p-6"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        border: '1px solid var(--border-light)'
                      }}
                    >
                      <h3 className="heading-3 mb-4">Guest Information</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Name:</span>
                          <span className="body-regular" data-testid="confirm-name">{bookingData.firstName} {bookingData.lastName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Email:</span>
                          <span className="body-regular" data-testid="confirm-email">{bookingData.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Phone:</span>
                          <span className="body-regular" data-testid="confirm-phone">{bookingData.phone}</span>
                        </div>
                      </div>
                    </div>

                    <p className="body-small text-center" style={{ color: 'var(--text-secondary)' }}>
                      A confirmation email will be sent to your email address.
                    </p>

                    <label className="flex items-start gap-3 cursor-pointer mt-4">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="mt-1 h-4 w-4 accent-[var(--interactive-base)]"
                        data-testid="terms-checkbox"
                      />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        I have read and accept the{' '}
                        <Link to="/terms" target="_blank" className="underline" style={{ color: 'var(--interactive-base)' }}>
                          Terms &amp; Conditions
                        </Link>
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {step > 1 && (
                  <Button
                    onClick={handleBack}
                    variant="outline"
                    className="flex items-center gap-2"
                    style={{ borderRadius: 0 }}
                    data-testid="back-btn"
                  >
                    <ArrowLeft size={16} />
                    Back
                  </Button>
                )}
                {step < 3 ? (
                  <Button
                    onClick={handleNext}
                    className="btn-primary ml-auto flex items-center gap-2"
                    data-testid="next-btn"
                  >
                    Next
                    <ArrowRight size={16} />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    className="btn-primary ml-auto flex items-center gap-2"
                    disabled={submitting || !termsAccepted}
                    data-testid="confirm-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        Confirm Booking
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Booking Summary Sidebar */}
            <div className="lg:col-span-1">
              <div
                className="p-6 sticky top-24"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-light)'
                }}
              >
                <h3 className="heading-3 mb-6">Booking Summary</h3>
                
                {selectedRoom && (
                  <>
                    <div className="mb-6">
                      <img
                        src={selectedRoom.images[0]}
                        alt={selectedRoom.name}
                        className="w-full h-40 object-cover mb-4"
                        style={{ border: '1px solid var(--border-light)' }}
                      />
                      <h4 className="heading-3 mb-2">{selectedRoom.name}</h4>
                      <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {selectedRoom.shortDescription}
                      </p>
                    </div>

                    {/* Guest summary */}
                    <div className="mb-4 pb-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: 'var(--text-secondary)' }}>Adults</span>
                        <span>{adults}</span>
                      </div>
                      {childrenAges.length > 0 && (
                        <div className="flex justify-between text-sm mt-1">
                          <span style={{ color: 'var(--text-secondary)' }}>Children</span>
                          <span>{childrenAges.length}</span>
                        </div>
                      )}
                    </div>

                    {nights > 0 && billingResult && (
                      <div className="space-y-3 mb-6 pb-6" style={{ borderBottom: '1px solid var(--border-light)' }}>
                        {calculatingPrice ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="animate-spin" size={20} style={{ color: 'var(--text-secondary)' }} />
                            <span className="ml-2 body-small" style={{ color: 'var(--text-secondary)' }}>Calculating...</span>
                          </div>
                        ) : (
                          <>
                            {/* Base Rate */}
                            <div className="flex justify-between">
                              <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                                Base Rate ({nights} night{nights > 1 ? 's' : ''})
                              </span>
                              <span className="body-regular">
                                {getCurrencySymbol(currency)}{(billingResult.breakdown?.base_rate || 0).toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Extra Adults */}
                            {billingResult.breakdown?.extra_adults > 0 && (
                              <div className="flex justify-between">
                                <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                                  Extra Adults
                                </span>
                                <span className="body-regular">
                                  {getCurrencySymbol(currency)}{billingResult.breakdown.extra_adults.toFixed(2)}
                                </span>
                              </div>
                            )}
                            
                            {/* Children */}
                            {billingResult.breakdown?.children > 0 && (
                              <div className="flex justify-between">
                                <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                                  Children
                                </span>
                                <span className="body-regular">
                                  {getCurrencySymbol(currency)}{billingResult.breakdown.children.toFixed(2)}
                                </span>
                              </div>
                            )}
                            
                            {/* Promotion Discount */}
                            {billingResult.breakdown?.promotion_discount > 0 && (
                              <div className="flex justify-between" style={{ color: 'var(--interactive-base)' }}>
                                <span className="body-small">
                                  {billingResult.applied_promotion?.auto_applied
                                    ? `${billingResult.applied_promotion.name} (auto)`
                                    : 'Promo Discount'}
                                </span>
                                <span className="body-regular">
                                  -{getCurrencySymbol(currency)}{billingResult.breakdown.promotion_discount.toFixed(2)}
                                </span>
                              </div>
                            )}
                            
                            {/* Add-ons */}
                            {billingResult.breakdown?.addons?.length > 0 && (
                              <>
                                {billingResult.breakdown.addons.map((addon, idx) => (
                                  <div key={idx} className="flex justify-between">
                                    <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                                      {addon.name}
                                      <span className="text-xs ml-1">
                                        ({addon.pricing_type === 'PER_PERSON' 
                                          ? `${addon.quantity} ppl` 
                                          : `x${addon.quantity}`}
                                        {addon.per_night && addon.nights_applied > 0 
                                          ? ` × ${addon.nights_applied}n` 
                                          : ''})
                                      </span>
                                    </span>
                                    <span className="body-regular">
                                      {getCurrencySymbol(currency)}{addon.total.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </>
                            )}
                            
                            {/* Subtotal */}
                            <div className="flex justify-between pt-2" style={{ borderTop: '1px dashed var(--border-light)' }}>
                              <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                                Subtotal
                              </span>
                              <span className="body-regular">
                                {getCurrencySymbol(currency)}{billingResult.subtotal?.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Taxes */}
                            {billingResult.breakdown?.taxes?.map((tax, idx) => (
                              <div key={idx} className="flex justify-between">
                                <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                                  {tax.name} ({tax.rate_percent}%)
                                </span>
                                <span className="body-regular">
                                  {getCurrencySymbol(currency)}{tax.amount.toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}

                    {nights > 0 && !billingResult && (
                      <div className="space-y-3 mb-6 pb-6" style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                            {getCurrencySymbol(currency)}{selectedRoom.price[currency]} x {nights} night{nights > 1 ? 's' : ''}
                          </span>
                          <span className="body-regular">{getCurrencySymbol(currency)}{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Tax (2%)</span>
                          <span className="body-regular">{getCurrencySymbol(currency)}{tax.toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between mb-6">
                      <span className="heading-3">Total</span>
                      <span className="heading-2" data-testid="booking-total">
                        {calculatingPrice ? (
                          <span style={{ color: 'var(--text-secondary)' }}>...</span>
                        ) : (
                          `${getCurrencySymbol(currency)}${total.toFixed(2)}`
                        )}
                      </span>
                    </div>
                  </>
                )}

                <p className="text-meta text-center" style={{ color: 'var(--text-light)' }}>
                  Prices are locked once booking is confirmed
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Method Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md" style={{ backgroundColor: 'var(--bg-primary)' }}>
          <DialogHeader>
            <DialogTitle className="heading-3">Select Payment Method</DialogTitle>
            <DialogDescription className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              Choose how you would like to pay for your booking
              {isMockMode && (
                <span className="block mt-2 text-amber-600 text-sm">
                  ⚠️ Demo Mode - No real payment will be processed
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <RadioGroup
              value={paymentMethod}
              onValueChange={setPaymentMethod}
              className="space-y-3"
            >
              {/* Web Payment Option */}
              <div 
                className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                  paymentMethod === 'web' ? 'border-[var(--olive-primary)] bg-[var(--bg-secondary)]' : 'border-[var(--border-light)]'
                }`}
                onClick={() => setPaymentMethod('web')}
              >
                <RadioGroupItem value="web" id="web" />
                <CreditCard size={24} style={{ color: 'var(--olive-primary)' }} />
                <div className="flex-1">
                  <Label htmlFor="web" className="body-regular font-medium cursor-pointer">
                    Visa / Mastercard (USD)
                  </Label>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Pay securely via Paynow using Visa or Mastercard
                  </p>
                </div>
              </div>

              {/* EcoCash Option */}
              <div 
                className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                  paymentMethod === 'ecocash' ? 'border-[var(--olive-primary)] bg-[var(--bg-secondary)]' : 'border-[var(--border-light)]'
                }`}
                onClick={() => setPaymentMethod('ecocash')}
              >
                <RadioGroupItem value="ecocash" id="ecocash" />
                <Smartphone size={24} style={{ color: '#4CAF50' }} />
                <div className="flex-1">
                  <Label htmlFor="ecocash" className="body-regular font-medium cursor-pointer">
                    EcoCash (USD)
                  </Label>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Pay using EcoCash mobile money in USD
                  </p>
                </div>
              </div>

            </RadioGroup>

            {/* Phone Number Input for EcoCash */}
            {paymentMethod === 'ecocash' && (
              <div className="mt-4">
                <Label htmlFor="mobilePhone" className="body-regular">
                  EcoCash Phone Number
                </Label>
                <Input
                  id="mobilePhone"
                  type="tel"
                  placeholder="e.g., 0771234567"
                  value={mobilePaymentPhone}
                  onChange={(e) => setMobilePaymentPhone(e.target.value)}
                  className="mt-2"
                  style={{ borderColor: 'var(--border-medium)' }}
                />
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Enter the phone number registered with EcoCash
                </p>
              </div>
            )}

            {/* Paynow Trust Badge */}
            <div className="mt-4 pt-4 flex items-center justify-center" style={{ borderTop: '1px solid var(--border-light)' }}>
              <img
                src="/assets/paynowbadge.svg"
                alt="Secured by Paynow"
                style={{ height: '100px', width: 'auto' }}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPaymentDialog(false);
                navigate(`/booking-confirmation?ref=${createdBookingRef}`);
              }}
              disabled={processingPayment}
              style={{ borderColor: 'var(--border-medium)' }}
            >
              Pay Later
            </Button>
            <Button
              onClick={handlePayment}
              disabled={processingPayment || (paymentMethod === 'ecocash' && !mobilePaymentPhone)}
              style={{ backgroundColor: 'var(--olive-primary)' }}
            >
              {processingPayment ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                `Pay ${getCurrencySymbol(currency)}${total.toFixed(2)}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingPage;
